import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import axiosInstance from '../../../../../interceptor/axiosInstance';
import { toast } from '../toastUtils';

const AssessmentEditbrn = ({
  fetchAssessments,
  selectedTest,
  setShowPopup,
}) => {

  const [startDate, setStartDate] = useState(selectedTest?.start_date_time || '');
  const [endDate, setEndDate] = useState(selectedTest?.end_date_time || '');
  const [duration, setDuration] = useState(selectedTest?.duration || '');
  const [isCheck, setIsCheck] = useState(selectedTest?.is_active || false);
  const [isRandomized, setIsRandomized] = useState(selectedTest?.is_randomized || false);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    if (selectedTest) {
      setStartDate(selectedTest.start_date_time ? selectedTest.start_date_time.slice(0, 16) : '');
      setEndDate(selectedTest.end_date_time ? selectedTest.end_date_time.slice(0, 16) : '');
      setDuration(selectedTest.duration || '');
      setIsCheck(selectedTest.is_active || false);
      setIsRandomized(selectedTest.is_randomized || false);
    }
  }, [selectedTest]);

  // Convert UTC to IST with offset
  const convertUTCToISTWithOffset = (utcDate) => {
    const date = new Date(utcDate);
    const offset = 5.5 * 60 * 60 * 1000; // IST offset
    const istDate = new Date(date.getTime() + offset);
    const formattedDate = istDate.toISOString().slice(0, 19);
    return `${formattedDate}+05:30`;
  };

  const handleUpdateTest = async () => {
    setLoading(true); // Start loader
    try {
      const fieldsToUpdate = {
        start_date_time: startDate ? convertUTCToISTWithOffset(startDate) : '',
        end_date_time: endDate ? convertUTCToISTWithOffset(endDate) : '',
        duration,
        is_active: isCheck,
        is_randomized: isRandomized,
      };
  
      const updatedFields = {};
  
      Object.keys(fieldsToUpdate).forEach((key) => {
        if (fieldsToUpdate[key] !== selectedTest[key]) {
          updatedFields[key] = fieldsToUpdate[key];
        }
      });
  
      if (file) {
        updatedFields['test_file'] = file;
      }
  
      if (Object.keys(updatedFields).length === 0) {
        toast.info('No changes detected.');
        setLoading(false);
        return;
      }
  
      let payload;
      let headers = {};
  
      if (file) {
        payload = new FormData();
        Object.entries(updatedFields).forEach(([key, value]) => {
          payload.append(key, value);
        });
        headers['Content-Type'] = 'multipart/form-data';
      } else {
        payload = updatedFields;
      }
  
      const response = await axiosInstance.put(
        `/assessments/tests/${selectedTest.id}/`,
        payload,
        { headers }
      );
  
      if (response.status >= 200 && response.status < 300) {
        toast.success('Test updated successfully!');
        fetchAssessments();
        setShowPopup(false);
      }
    } catch (error) {
      console.error('Error updating test', error);
      const errorMessage = error.response?.data?.message || 'Failed to update the test.';
      const validationErrors = error.response?.data?.errors;
  
      const formattedErrors = validationErrors
        ? Object.entries(validationErrors)
            .map(([key, value]) => `${key}: ${value.join(', ')}`)
            .join(' | ')
        : '';
  
      toast.error(formattedErrors ? `${errorMessage}: ${formattedErrors}` : errorMessage);
    } finally {
      setLoading(false); // Stop loader
    }
  };
  

  return ReactDOM.createPortal(
    <div className="edit-test-modal-overlay" onClick={() => !loading && setShowPopup(false)}>
      <div className="edit-test-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="edit-test-modal-header">
          <div className="edit-test-modal-title">
            <span className="edit-test-modal-icon">📝</span>
            <h2>Edit Test</h2>
          </div>
          <button 
            className="edit-test-modal-close" 
            onClick={() => setShowPopup(false)}
            disabled={loading}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="edit-test-modal-body">
          <form className="edit-test-form" onSubmit={(e) => { e.preventDefault(); handleUpdateTest(); }}>
            {/* Test Name Display */}
            <div className="edit-test-info-card">
              <div className="edit-test-info-label">Test Name:</div>
              <div className="edit-test-info-value">{selectedTest?.name || 'Untitled Test'}</div>
            </div>

            {/* Start Date */}
            <div className="edit-test-form-group">
              <label htmlFor="startDate" className="edit-test-form-label">
                Start Date & Time
              </label>
              <input
                type="datetime-local"
                id="startDate"
                className="edit-test-form-input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
              />
            </div>

            {/* End Date */}
            <div className="edit-test-form-group">
              <label htmlFor="endDate" className="edit-test-form-label">
                End Date & Time
              </label>
              <input
                type="datetime-local"
                id="endDate"
                className="edit-test-form-input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={
                  startDate
                    ? new Date(new Date(startDate).getTime() + 1 * 60000).toISOString().slice(0, 16)
                    : new Date().toISOString().slice(0, 16)
                }
              />
            </div>

            {/* Duration */}
            <div className="edit-test-form-group">
              <label htmlFor="duration" className="edit-test-form-label">
                Duration (minutes) <span className="edit-test-required">*</span>
              </label>
              <input
                type="number"
                id="duration"
                className="edit-test-form-input"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                min="1"
                placeholder="Enter duration in minutes"
                required
              />
            </div>

            {/* Checkboxes */}
            <div className="edit-test-form-row">
              <div className="edit-test-checkbox-group">
                <label className="edit-test-checkbox-label">
                  <input
                    type="checkbox"
                    checked={isCheck}
                    onChange={(e) => setIsCheck(e.target.checked)}
                    className="edit-test-checkbox"
                  />
                  <span className="edit-test-checkbox-text">Is Active</span>
                </label>
              </div>

              <div className="edit-test-checkbox-group">
                <label className="edit-test-checkbox-label">
                  <input
                    type="checkbox"
                    checked={isRandomized}
                    onChange={(e) => setIsRandomized(e.target.checked)}
                    className="edit-test-checkbox"
                  />
                  <span className="edit-test-checkbox-text">Randomize Questions</span>
                </label>
              </div>
            </div>

            {/* File Upload */}
            <div className="edit-test-form-group">
              {selectedTest?.test_file && (
                <div className="edit-test-current-file">
                  <span className="edit-test-file-icon">📄</span>
                  <span className="edit-test-file-name">
                    Current: {selectedTest.test_file.split('/').pop()}
                  </span>
                </div>
              )}
              <label htmlFor="fileUpload" className="edit-test-form-label">
                Upload New Assessment File (.xlsx)
              </label>
              <input
                type="file"
                id="fileUpload"
                className="edit-test-form-file"
                accept=".xlsx"
                onChange={(e) => setFile(e.target.files[0])}
              />
              {file && (
                <div className="edit-test-new-file">
                  <span className="edit-test-file-icon">📎</span>
                  <span className="edit-test-file-name">Selected: {file.name}</span>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="edit-test-modal-footer">
          <button 
            type="button"
            className="edit-test-btn-cancel" 
            onClick={() => setShowPopup(false)}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            type="button"
            className="edit-test-btn-submit" 
            onClick={handleUpdateTest}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="edit-test-spinner"></span>
                Saving Changes...
              </>
            ) : (
              <>
                <span>💾</span>
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AssessmentEditbrn;
